<script setup lang="ts">
import type { OauthParameters } from "../composables/useAuthentication"

definePageMeta({ layout: "auth" })

const { state: authState, logIn, authCode, toReadableError } = useAuthentication()
const { authErrorMessageTestId } = useTestId()
const { hasStoredUserCredentials } = useSelf()
const { query } = useRoute()
const formRef = ref<HTMLFormElement>()

const model = useState("login-details", () => ({
  isLoading: false,
  error: undefined as string | undefined,
}))

model.value.isLoading = false
model.value.error = undefined

function getOauthParameters(): OauthParameters | undefined {
  if ("redirect_uri" in query)
    return query as any as OauthParameters
  return // FIXME
}

async function onSubmit() {
  const oauth = getOauthParameters()
  if (isDefined(oauth))
    return await sendOauthLogin()
  else
    return await sendLogIn()
}

async function sendOauthLogin() {
  const parameters = getOauthParameters()
  if (!isDefined(parameters))
    throw new Error("Cannot call this outside of oauth flow")
  const { email, password } = authState.value
  try {
    model.value.error = undefined
    model.value.isLoading = true
    await logIn({ email, password })
    return await authCode(parameters)
  }
  catch (error) {
    console.error("ERROR", error)
    model.value.error = toReadableError(error)
    model.value.isLoading = false
  }
}

async function sendLogIn() {
  const { email, password } = authState.value
  try {
    model.value.error = undefined
    model.value.isLoading = true
    await logIn({ email, password })
    return await navigateTo("/home")
  }
  catch (error) {
    console.error("ERROR", error)
    model.value.error = toReadableError(error)
    model.value.isLoading = false
  }
}

onMounted(() => {
  // we are already logged in (i.e. we already have a client token),
  // go ahead and directly take the next step
  if (hasStoredUserCredentials()) {
    const parameters = getOauthParameters()
    if (parameters)
      return authCode(parameters)
    else
      return navigateTo("/home")
  }
})

useHead({
  title: "Login",
})
</script>

<template>
  <div class="min-h-full flex flex-col items-center justify-center px-4 py-[50px]">
    <div class="w-sm">
      <div class="mb-12 space-y-3">
        <div class="mx-auto w-16">
          <img class="" src="/favicon.png">
        </div>
        <div class="text-center text-3xl">
          Welcome back
        </div>
        <div class="text-center text-base">
          <nuxt-link :to="{ name: 'signup' }" class="group">
            Looking to <span class="hover-link">sign up</span> with your invite code instead?
          </nuxt-link>
        </div>
      </div>
      <form ref="formRef" @submit.prevent="onSubmit()">
        <auth-input
          v-model="authState.email"
          label="E-mail"
          autocomplete="email"
          placeholder="Enter e-mail..."
          autofocus
        />

        <auth-input
          v-model="authState.password"
          label="Password"
          type="password"
          autocomplete="current-password"
          placeholder="Enter password..."
        />
        <div class="h-6" />
        <div class="space-y-3">
          <auth-button
            type="submit"
            class="h-10 w-full"
            :loading="model.isLoading"
          >
            Log In
          </auth-button>
          <div class="h-4 w-full text-center text-sm font-medium text-$global-amber-primary">
            <div v-if="model.error" :data-testid="authErrorMessageTestId">
              {{ model.error }}
            </div>
          </div>
        </div>
      </form>
      <nuxt-link to="/forgot-password">
        <div class="group mx-auto mt-3 w-3/4 text-center text-sm">
          Forgot your password? <span class="hover-link">
            Send a reset code.
          </span>
        </div>
      </nuxt-link>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
.hover-link
  @apply font-medium text-$global-blue-primary group-hover:underline
</style>
